import React, { FC, useEffect, useState } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import errors from "go-app/services/errors";
import { ApiError } from "go-core/api/types";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { FormSelectGroup } from "go-form/components/FormSelect";
import {
	ProductApi,
	ProductConnectionApi,
	ProductConnectionSearchSelectApi,
} from "../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../services/Api/api";

interface Props {
	form: UseFormReturn<ProductApi>;
	formErrors: ApiError[];
}

const filterConnectionsErrors = (formErrors: ApiError[]) => {
	return formErrors.filter((error) => error.field?.includes("connections"));
};

const ProductPackagesForm: FC<Props> = ({ form, formErrors }) => {
	const { t } = useTranslation();
	const { control, watch, register, setValue } = form;
	const { remove, append } = useFieldArray({
		control,
		name: "connections",
		keyName: "key",
	});
	const watchedConnections = watch("connections");
	const connectionsAsString = JSON.stringify(watchedConnections);
	const watchedMeasureType = watch("measure_type");
	const [connectionsErrors, setConnectionsErrors] = useState(filterConnectionsErrors(formErrors));
	const { isMobile } = useWindowSize();

	useEffect(() => {
		setConnectionsErrors(filterConnectionsErrors(formErrors));
	}, [formErrors]);

	const checkIfAddNewConnection = () => {
		if (!watchedConnections) return false;
		const prevConnection = watchedConnections[watchedConnections?.length - 1];
		return prevConnection?.name;
	};

	useEffect(() => {
		if (checkIfAddNewConnection())
			append({
				id: new Date().getTime(),
				package_uid: "",
				name: "",
				barcodes: [],
			});
	}, [connectionsAsString]);

	const onRemove = (index: number) => {
		remove(index);
		setConnectionsErrors([]);
	};

	const onSearchPackage = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getProductPackageSearchSelect(t, obj, {
			cancelToken: options?.token,
		});
	};

	const onChangeConnection = (obj: ProductConnectionSearchSelectApi, index: number) => {
		if (!obj) {
			remove(index);
		} else {
			setValue(`connections.${index}.id`, obj.id);
			setValue(`connections.${index}.package_uid`, obj.value);
			setValue(`connections.${index}.barcodes`, []);
		}
	};

	const getConnectionLabel = (opt: ProductConnectionSearchSelectApi, connection: ProductConnectionApi): string => {
		if (Object?.keys(connection)?.length > 0 && opt.label === connection.name) {
			let additionalInfo = "";
			if (connection?.name) {
				if (connection?.barcodes?.length > 0) {
					additionalInfo += ` (`;
					connection.barcodes.forEach((barcode, index) => {
						additionalInfo += `${barcode?.barcode} - ${barcode?.volume}${t(
							`enums.products.measure_types.${watchedMeasureType}`
						)}`;
						if (index !== connection.barcodes.length - 1) additionalInfo += ", ";
					});
					additionalInfo += `)`;
				}
			}
			return connection?.name + additionalInfo;
		}
		return opt.label;
	};

	const checkIfConnectionHasError = (index: number) =>
		connectionsErrors.some((error) => error?.field?.includes(`[${index}]`));

	return (
		<fieldset className="form-group">
			<div className={isMobile ? "table-responsive" : ""}>
				<table className="table table-form positions-table w-100">
					<thead>
						<tr>
							<th className="action" />
							<th>{t("modules.product.field.package.title")}</th>
							<th className="action" />
						</tr>
					</thead>
					<tbody>
						{watchedConnections?.map((item, index) => (
							<tr key={watchedConnections[index].id}>
								<td className="action">
									<FormInput
										type="hidden"
										errors={errors}
										register={register}
										name={`connections.${index}.id`}
										defaultValue={item.id}
									/>
								</td>
								<td>
									<FormSelectGroup
										errors={errors}
										control={control}
										name={`connections.${index}.name`}
										onChange={(value, obj) => onChangeConnection(obj, index)}
										getOptionLabel={(opt) => opt.label}
										getOptionValue={(opt) => opt.label}
										formatOptionLabel={(opt) => getConnectionLabel(opt, item)}
										defaultValue={{
											label: item?.name,
											value: item?.name,
										}}
										loadOptions={onSearchPackage}
										data-testid={`connections.${index}.name`}
									/>
									{checkIfConnectionHasError(index) && (
										<div className="invalid-feedback ms-0" style={{ display: "inline" }}>
											{t("constraints.productConnectionExist")}
										</div>
									)}
								</td>
								<td className="action">
									{!(index === 0 && index === watchedConnections.length - 1) && (
										<RemoveSVG className="icon" onClick={() => onRemove(index)} />
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</fieldset>
	);
};

export default ProductPackagesForm;
